import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../services/api'

const PARTS = [
  { key: 'v_head', label: 'Cabeza', max: 20, botBar: 'bg-emerald-400', enemyBar: 'bg-amber-400' },
  { key: 'v_torso', label: 'Torso', max: 40, botBar: 'bg-cyan-400', enemyBar: 'bg-red-500' },
  { key: 'v_arm_l', label: 'Hombro/Brazo izq.', max: 20, botBar: 'bg-emerald-400', enemyBar: 'bg-red-400' },
  { key: 'v_arm_r', label: 'Hombro/Brazo der.', max: 20, botBar: 'bg-emerald-400', enemyBar: 'bg-red-400' },
  { key: 'v_leg_l', label: 'Pierna izq.', max: 30, botBar: 'bg-violet-400', enemyBar: 'bg-fuchsia-400' },
  { key: 'v_leg_r', label: 'Pierna der.', max: 30, botBar: 'bg-violet-400', enemyBar: 'bg-fuchsia-400' }
]

const PART_MAX_BY_KEY = Object.fromEntries(PARTS.map((part) => [part.key, part.max]))

const DAMAGE_PART_IMAGES = {
  v_head: ['da-cab.png'],
  v_torso: ['da-tor.png'],
  v_arm_l: ['da-h-iz.png', 'da-m-iz.png'],
  v_arm_r: ['da-h-de.png', 'da-m-de.png'],
  v_leg_l: ['da-p-iz.png'],
  v_leg_r: ['da-p-de.png']
}

function clampPercent(value) {
  return Math.max(0, Math.min(100, Number(value || 0)))
}

function healthPercent(current, max) {
  if (!max) return 0
  return clampPercent((Number(current || 0) / Number(max || 1)) * 100)
}

function partLabel(part) {
  const labels = {
    v_head: 'Cabeza',
    v_torso: 'Torso',
    v_arm_l: 'Hombro/Brazo izquierdo',
    v_arm_r: 'Hombro/Brazo derecho',
    v_leg_l: 'Pierna izquierda',
    v_leg_r: 'Pierna derecha'
  }
  return labels[part] || part
}

function formatPartChange(entry) {
  if (!entry?.part || entry.before === undefined || entry.after === undefined) return null
  const max = Number(PART_MAX_BY_KEY[entry.part] || 0)
  const maxSuffix = max > 0 ? `/${max}` : ''
  return `${partLabel(entry.part)}: ${Number(entry.before)}${maxSuffix} -> ${Number(entry.after)}${maxSuffix}`
}

function historyTone(type) {
  if (String(type || '').includes('enemy')) return 'border-rose-500/30 bg-rose-500/10 text-rose-100'
  if (String(type || '').includes('medkit') || String(type || '').includes('heal')) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
  if (String(type || '').includes('ewar') || String(type || '').includes('system')) return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-100'
  return 'border-slate-600/60 bg-slate-900/70 text-slate-100'
}

function imageUrl(image) {
  if (!image) return null
  return `${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${image}`
}

function sharedImageUrl(path) {
  return `${import.meta.env.VITE_API_URL || ''}${path}`
}

function partValue(summary, key) {
  return Number(summary?.[key] || 0)
}

function renderDamagePanel({
  title,
  subtitle,
  parts,
  source,
  maxSource,
  summary,
  barKey,
  damageLabel
}) {
  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className={`text-xs uppercase tracking-[0.25em] ${barKey === 'enemyBar' ? 'text-red-300/70' : 'text-cyan-300/70'}`}>{title}</p>
          <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
        </div>
        <div className="text-right text-xs text-slate-400">
          <p>{damageLabel}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[190px_1fr]">
        <div className="relative mx-auto aspect-[3/4] w-full max-w-[190px] overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
          <img
            src={sharedImageUrl('/img/func-dano/bot-fo.png')}
            alt={title}
            className="absolute inset-0 h-full w-full object-contain"
          />
          {parts.flatMap((part) => {
            const current = Number(source?.[part.key] || 0)
            const max = Number(maxSource?.[part.key] || part.max || 0)
            const damage = partValue(summary, part.key)
            if (current >= max && damage <= 0) return []
            return (DAMAGE_PART_IMAGES[part.key] || []).map((image) => (
              <img
                key={`${part.key}-${image}`}
                src={sharedImageUrl(`/img/func-dano/${image}`)}
                alt=""
                className="absolute inset-0 h-full w-full object-contain"
              />
            ))
          })}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {parts.map((part) => {
            const current = Number(source?.[part.key] || 0)
            const max = Number(maxSource?.[part.key] || part.max || 0)
            const damage = partValue(summary, part.key)
            return (
              <div key={part.key} className="rounded-xl border border-slate-700 bg-slate-900/80 p-3">
                <div className="flex items-center justify-between text-sm text-slate-300 mb-1">
                  <span>{part.label}</span>
                  <span>{current}/{max}</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full ${part[barKey]}`} style={{ width: `${healthPercent(current, max)}%` }} />
                </div>
                <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                  {damageLabel}: {damage}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function Combat() {
  const navigate = useNavigate()
  const location = useLocation()
  const initialCombat = location.state?.combat || null
  const hasInitialCombat = Boolean(initialCombat)
  const [loading, setLoading] = useState(!initialCombat)
  const [combat, setCombat] = useState(initialCombat)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [resultMessage, setResultMessage] = useState('')
  const [selectedWeaponSlot, setSelectedWeaponSlot] = useState('auto')
  const [targetPart, setTargetPart] = useState('v_torso')
  const [medkitPart, setMedkitPart] = useState('v_torso')
  const historyEndRef = useRef(null)

  const loadCombat = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await api.get(`/combat/active?t=${Date.now()}`)
      const activeCombat = res.data?.combat || null
      if (!activeCombat) {
        if (hasInitialCombat) {
          setError('No se pudo confirmar el combate activo. Prueba a refrescar la lucha.')
          return
        }
        navigate('/game', { replace: true })
        return
      }
      setCombat(activeCombat)
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el combate activo')
    } finally {
      setLoading(false)
    }
  }, [hasInitialCombat, navigate])

  useEffect(() => {
    void loadCombat()
  }, [loadCombat])

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [combat?.history])

  const weaponSlots = combat?.loadout?.weaponSlots || []
  const medkits = combat?.loadout?.medkits || []
  const ammoShots = Number(combat?.loadout?.ammoShots || 0)
  const ammoMagazines = Number(combat?.loadout?.ammoMagazines || 0)
  const history = combat?.history || []
  const damageSummary = combat?.damageSummary || { dealtToEnemy: {}, receivedByBot: {} }
  const canAttack = weaponSlots.length > 0
  const bestWeapon = weaponSlots.reduce((best, weapon) => {
    if (!best) return weapon
    return Number(weapon.power || 0) > Number(best.power || 0) ? weapon : best
  }, null)
  const autoWeaponPower = Number(bestWeapon?.power || 0)
  const weaponTelemetry = combat?.weaponTelemetry || {}
  const selectedWeapon = selectedWeaponSlot === 'auto'
    ? bestWeapon
    : weaponSlots.find((weapon) => weapon.slot === selectedWeaponSlot) || bestWeapon
  const selectedTelemetry = selectedWeapon ? weaponTelemetry[selectedWeapon.slot] || null : null
  const selectedProfile = selectedTelemetry?.profile || selectedWeapon?.profile || null

  const handleAttack = useCallback(async () => {
    if (!combat || actionLoading) return

    setActionLoading(true)
    setError('')
    setResultMessage('')
    try {
      const res = await api.post('/combat/attack', {
        enemyInstanceId: combat.enemyInstanceId,
        targetPart,
        weaponSlot: selectedWeaponSlot === 'auto' ? '' : selectedWeaponSlot
      })

      const combatRes = res.data?.combat || {}
      if (combatRes.enemyDefeated || combatRes.botDefeated) {
        setResultMessage(res.data?.message || 'Combate resuelto')
        setTimeout(() => {
          if (combatRes.enemyDefeated) {
            navigate('/victory', {
              replace: true,
              state: {
                victory: {
                  ...combatRes,
                  message: res.data?.message || 'Combate resuelto',
                  bot: combat?.bot,
                  enemy: combat?.enemy
                }
              }
            })
            return
          }

          navigate('/game', { replace: true })
        }, 900)
        return
      }

      setResultMessage(res.data?.message || 'Ataque resuelto')
      await loadCombat()
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo resolver el ataque')
    } finally {
      setActionLoading(false)
    }
  }, [actionLoading, combat, loadCombat, navigate, selectedWeaponSlot, targetPart])

  const handleFlee = useCallback(async () => {
    if (!combat || actionLoading) return
    setActionLoading(true)
    setError('')
    setResultMessage('')
    try {
      const res = await api.post('/combat/flee', {
        enemyInstanceId: combat.enemyInstanceId
      })
      setResultMessage(res.data?.message || 'Has huido del combate')
      setTimeout(() => {
        navigate('/game', { replace: true })
      }, 600)
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo huir del combate')
    } finally {
      setActionLoading(false)
    }
  }, [actionLoading, combat, navigate])

  const handleMedkit = useCallback(async () => {
    if (!combat || actionLoading) return
    setActionLoading(true)
    setError('')
    setResultMessage('')
    try {
      const res = await api.post('/combat/action', {
        actionType: 'medkit',
        enemyInstanceId: combat.enemyInstanceId,
        targetPart: medkitPart
      })
      if (res.data?.combat?.botDefeated) {
        navigate('/game', { replace: true })
        return
      }
      setResultMessage(res.data?.message || 'Kit médico usado')
      await loadCombat()
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo usar el kit médico')
    } finally {
      setActionLoading(false)
    }
  }, [actionLoading, combat, loadCombat, medkitPart, navigate])

  const handleEwar = useCallback(async () => {
    if (!combat || actionLoading) return
    setActionLoading(true)
    setError('')
    setResultMessage('')
    try {
      const res = await api.post('/combat/action', {
        actionType: 'ewar',
        enemyInstanceId: combat.enemyInstanceId
      })
      setResultMessage(res.data?.message || 'Guerra electrónica activada')
      await loadCombat()
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo usar guerra electrónica')
    } finally {
      setActionLoading(false)
    }
  }, [actionLoading, combat, loadCombat])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">
        Restaurando combate...
      </div>
    )
  }

  if (!combat) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">
        No hay combate activo.
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(220,38,38,0.16),rgba(4,6,13,0.98)_55%)] text-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl rounded-3xl border border-red-500/20 bg-[#07111d] shadow-[0_0_60px_rgba(255,0,0,0.12)] overflow-hidden">
        <div className="px-6 py-5 border-b border-red-500/20 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-red-300/70">BOT combate</p>
            <h1 className="text-3xl font-black text-white mt-1">{combat.enemy?.name || 'Enemigo'}</h1>
            <p className="text-sm text-slate-300 mt-2">
              Sector #{combat.sectorId} | Coordenada {combat.coordinate}
            </p>
          </div>
          <div className="text-right text-sm text-slate-300">
            <p>Bot #{combat.bot?.id || '-'}</p>
            <p>Enemigo #{combat.enemy?.id || '-'}</p>
            <p className="text-cyan-200 mt-1">
              Balas: {ammoShots} | Cargadores: {ammoMagazines}
            </p>
          </div>
        </div>

        <div className="px-6 pt-6">
          <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-300/70">Historial del combate</p>
            <div className="mt-4 max-h-[260px] overflow-y-auto space-y-3 pr-1">
              {history.length > 0 ? history.map((entry) => {
                const partChange = formatPartChange(entry)
                return (
                  <div key={entry.id} className={`rounded-xl border px-4 py-3 text-sm ${historyTone(entry.type)}`}>
                    <div className="flex items-center justify-between gap-3">
                      <strong className="text-sm">{entry.title || entry.type}</strong>
                      <span className="text-[11px] uppercase tracking-[0.2em] opacity-70">{entry.actor || 'system'}</span>
                    </div>
                    <p className="mt-2 opacity-90">{entry.detail}</p>
                    {(partChange || entry.damage !== undefined || entry.ammoSpent !== undefined) && (
                      <p className="mt-2 text-xs uppercase tracking-[0.18em] opacity-70">
                        {partChange || (entry.part ? partLabel(entry.part) : 'General')}
                        {entry.damage !== undefined ? ` | ${entry.damage > 0 ? `-${entry.damage}` : `+${Math.abs(entry.damage)}`}` : ''}
                        {entry.ammoSpent !== undefined ? ` | ${entry.ammoSpent} balas` : ''}
                      </p>
                    )}
                  </div>
                )
              }) : (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-400">
                  A�n no hay eventos registrados.
                </div>
              )}
              <div ref={historyEndRef} />
            </div>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr] p-6 pt-4">
          <div className="space-y-4">
            {renderDamagePanel({
              title: 'Tu bot',
              subtitle: `Turno: ${combat.turn || 'enemy'} | Ronda ${combat.round || 1} | Escudo ${Number(combat.bot?.shield_points || 0)}`,
              parts: PARTS,
              source: combat.bot,
              maxSource: combat.botMax || null,
              summary: damageSummary.receivedByBot,
              barKey: 'botBar',
              damageLabel: 'Recibido'
            })}

            <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-300/70">Selector de ataque</p>

              <div className="mt-4">
                <span className="block text-sm text-slate-300 mb-3">Armas equipadas</span>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {canAttack ? (
                    <>
                      <button
                        type="button"
                        disabled={actionLoading}
                        onClick={() => setSelectedWeaponSlot('auto')}
                        className={`rounded-2xl border px-4 py-3 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${selectedWeaponSlot === 'auto' ? 'border-amber-400 bg-amber-500/15 ring-1 ring-amber-400/40' : 'border-slate-700 bg-slate-900/80 hover:border-slate-500'}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold text-white">Auto</p>
                            <p className="text-xs text-slate-400 mt-1">Mejor arma disponible según potencia y control</p>
                          </div>
                          <div className="text-right text-xs text-amber-200">
                            <p>Daño</p>
                            <p className="text-lg font-black">+{autoWeaponPower || 0}</p>
                          </div>
                        </div>
                      </button>

                      {weaponSlots.map((weapon) => {
                        const active = selectedWeaponSlot === weapon.slot
                        return (
                          <button
                            type="button"
                            key={weapon.slot}
                            disabled={actionLoading}
                            onClick={() => setSelectedWeaponSlot(weapon.slot)}
                            className={`rounded-2xl border px-4 py-3 text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${active ? 'border-cyan-400 bg-cyan-500/15 ring-1 ring-cyan-400/40' : 'border-slate-700 bg-slate-900/80 hover:border-slate-500'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-slate-700 bg-slate-950 flex items-center justify-center">
                                {weapon.image ? (
                                  <img src={imageUrl(weapon.image)} alt={weapon.name} className="h-full w-full object-cover" />
                                ) : (
                                  <span className="text-xs text-slate-400">SIN IMG</span>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-white truncate">{weapon.name}</p>
                                <p className="text-xs text-slate-400 mt-1">{weapon.slot.toUpperCase()}</p>
                                <p className="text-[11px] text-slate-500 mt-1">
                                  Precisión {Math.round(Number(weapon.profile?.accuracy || 0) * 100)}% · Alcance {Number(weapon.profile?.maxRange || 1)}
                                </p>
                              </div>
                              <div className="text-right text-xs text-cyan-200">
                                <p>Daño</p>
                                <p className="text-lg font-black">+{Number(weapon.power || 0)}</p>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </>
                  ) : (
                    <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-5 text-sm text-rose-100">
                      Sin armas operativas.
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <label className="block">
                  <span className="block text-sm text-slate-300 mb-2">Objetivo</span>
                  <select
                    value={targetPart}
                    onChange={(e) => setTargetPart(e.target.value)}
                    className="w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100"
                  >
                    <option value="v_head">Cabeza</option>
                    <option value="v_torso">Torso</option>
                    <option value="v_arm_l">Hombro/Brazo izquierdo</option>
                    <option value="v_arm_r">Hombro/Brazo derecho</option>
                    <option value="v_leg_l">Pierna izquierda</option>
                    <option value="v_leg_r">Pierna derecha</option>
                  </select>
                </label>
              </div>

              {selectedWeapon && selectedProfile && (
                <div className="mt-4 rounded-2xl border border-slate-700 bg-slate-900/70 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{selectedWeapon.name}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Precisión {Math.round(Number(selectedProfile.accuracy || 0) * 100)}% · Alcance {Number(selectedProfile.maxRange || 1)} · Distancia óptima {Number(selectedProfile.optimalRange || 1)}
                      </p>
                    </div>
                    <div className="text-right text-xs text-cyan-200">
                      <p>Calor</p>
                      <p className="text-lg font-black">{Math.round(Number(selectedTelemetry?.heat || 0))}</p>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-xl border border-slate-700 bg-slate-950/70 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Daño base</p>
                      <p className="mt-1 text-lg font-black text-white">+{Number(selectedWeapon.power || 0)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-700 bg-slate-950/70 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Calor por tiro</p>
                      <p className="mt-1 text-lg font-black text-amber-200">{Number(selectedProfile.heatPerShot || 0)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-700 bg-slate-950/70 px-3 py-2">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Riesgo atasco</p>
                      <p className="mt-1 text-lg font-black text-rose-200">{Math.round(Number(selectedProfile.jamChance || 0) * 100)}%</p>
                    </div>
                  </div>

                  <div className="mt-3 h-2.5 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full ${selectedTelemetry?.isJammed ? 'bg-rose-500' : 'bg-amber-400'}`}
                      style={{ width: `${Math.min(100, Math.round(Number(selectedTelemetry?.heatPercent || 0)))}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    {selectedTelemetry?.isJammed
                      ? 'Arma encasquillada: necesita enfriarse un turno antes de volver a disparar.'
                      : `Umbral de sobrecalentamiento: ${Number(selectedProfile.overheatThreshold || 0)}. Enfria ${Number(selectedProfile.coolPerRound || 0)} por turno.`}
                  </p>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-3">
                {canAttack && (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => void handleAttack()}
                    className="rounded-xl border border-rose-500 bg-rose-600 px-5 py-3 text-white font-semibold hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Resolviendo...' : 'Atacar'}
                  </button>
                )}
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => void handleFlee()}
                  className="rounded-xl border border-slate-600 bg-slate-800 px-5 py-3 text-slate-100 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Huir
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => void handleEwar()}
                  className="rounded-xl border border-cyan-600 bg-cyan-600/20 px-5 py-3 text-cyan-100 hover:bg-cyan-600/30 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Guerra electrónica
                </button>
                <button
                  type="button"
                  onClick={() => void loadCombat()}
                  className="rounded-xl border border-slate-600 bg-slate-800 px-5 py-3 text-slate-100 hover:bg-slate-700"
                >
                  Refrescar
                </button>
              </div>

              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-slate-500">
                Si usas un kit médico, consumes tu turno y el enemigo responde.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-950/70 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-300/70">Kits médicos</p>
              <div className="mt-4">
                <label className="block">
                  <span className="block text-sm text-slate-300 mb-2">Parte a reparar</span>
                  <select
                    value={medkitPart}
                    onChange={(e) => setMedkitPart(e.target.value)}
                    className="w-full rounded-xl border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100"
                  >
                    <option value="v_head">Cabeza</option>
                    <option value="v_torso">Torso</option>
                    <option value="v_arm_l">Hombro/Brazo izquierdo</option>
                    <option value="v_arm_r">Hombro/Brazo derecho</option>
                    <option value="v_leg_l">Pierna izquierda</option>
                    <option value="v_leg_r">Pierna derecha</option>
                  </select>
                </label>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {medkits.length > 0 ? medkits.map((kit, index) => (
                  <button
                    key={`${kit.inventoryId}-${index}`}
                    type="button"
                    disabled={actionLoading}
                    onClick={() => void handleMedkit()}
                    className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-left hover:bg-emerald-500/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-emerald-500/30 bg-slate-950 flex items-center justify-center">
                        {kit.image ? (
                          <img src={imageUrl(kit.image)} alt={kit.name} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-xs text-emerald-200">KIT</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-white truncate">{kit.name}</p>
                        <p className="text-xs text-emerald-200/80 mt-1">
                          x{kit.quantity} · Cura {Number(kit.power || 0)} HP
                        </p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs uppercase tracking-[0.2em] text-emerald-100/70">
                      Pulsa para usarlo
                    </p>
                  </button>
                )) : (
                  <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-4 text-sm text-slate-400">
                    No tienes kits médicos disponibles.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {renderDamagePanel({
              title: 'Enemigo',
              subtitle: `Nivel: ${combat.enemy?.level || '-'} | Recompensa XP: ${combat.enemy?.experience_reward || 0}`,
              parts: PARTS,
              source: combat.enemy,
              maxSource: combat.enemyMax || null,
              summary: damageSummary.dealtToEnemy,
              barKey: 'enemyBar',
              damageLabel: 'Causado'
            })}


          </div>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {resultMessage && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-emerald-200 text-sm">
              {resultMessage}
            </div>
          )}
          {error && (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-rose-200 text-sm">
              {error}
            </div>
          )}

          <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
            La lucha se guarda en tiempo real. Puedes salir y volver sin perder el estado.
          </p>
        </div>
      </div>
    </div>
  )
}






